import {Autocomplete, CircularProgress, TextField} from '@mui/material';
import {FC, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {ISelectOption} from '../../../api/strategies/option.type';
import {useSearchStrategy} from '../../../hooks/useSearchStrategy';
import {AddressItemType} from '../../../shared/types/addressItem.types';
import {searchAddressItemStrategy} from '../../../api/strategies/searchAddressItemStrategy';
import {searchWorkersForFilterStrategy} from '../../../api/strategies/searchWorkersStrategy';
import {FilterNameType} from '../../../shared/types/filtersSet.types';
import {isObject} from 'lodash';
import {IReportFilterValue} from './ReportFilter';

interface Props {
  filterByWatch: FilterNameType | string;
}

type DictSearchType = {
  [key: string]: {
    searchStrategy: (arg: string) => Promise<unknown[]>;
  };
};

const DICT_SEARCH: DictSearchType = {
  city: {
    searchStrategy: (
      (type: AddressItemType) => (str: string) =>
        searchAddressItemStrategy(str, type)
    )('CITY'),
  },
  region: {
    searchStrategy: (
      (type: AddressItemType) => (str: string) =>
        searchAddressItemStrategy(str, type)
    )('REGION'),
  },
  street: {
    searchStrategy: (
      (type: AddressItemType) => (str: string) =>
        searchAddressItemStrategy(str, type)
    )('STREET'),
  },
  workerId: {
    searchStrategy: (str: string) => searchWorkersForFilterStrategy(str),
  },
} as const;

export const DictSearchField: FC<Props> = ({filterByWatch}) => {
  const [openSelect, setOpenSelect] = useState(false);
  const {setValue, getValues, getFieldState} = useFormContext<Pick<IReportFilterValue, 'value'>>();

  const value = getValues('value');
  const {error} = getFieldState('value');

  const {handleSearch, data, searchLoading, searchTerm} = useSearchStrategy<unknown>(
    DICT_SEARCH[filterByWatch]?.searchStrategy || null,
  );

  return (
    <Autocomplete
      open={openSelect}
      onOpen={() => {
        setOpenSelect(true);
      }}
      onClose={() => {
        setOpenSelect(false);
      }}
      onChange={(_, newValue) => {
        isObject(newValue) && setValue('value', newValue);
      }}
      value={value as ISelectOption}
      getOptionLabel={(option) => option?.label ?? ''}
      inputValue={searchTerm}
      onInputChange={(_, newInputValue) => {
        handleSearch(newInputValue);
      }}
      options={data as ISelectOption[]}
      loading={searchLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          error={!!error}
          helperText={error ? error.message : ''}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searchLoading && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
